/* ATCMenu.tsx */
import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import type AircraftModel from "../model/AircraftModel";
import { setCurrentAircraftId } from "../model/CurrentAircraft";
import {
	acceptFlight,
	handlePublishPromise,
	persistFrontendFlightController,
} from "../mqtt-client/publishers";
import {
	aircraftStore,
	configurationStore,
	cwpStore,
	datablockStore,
	sepQdmStore,
	simulatorStore,
	trajectoryPredictionStore,
} from "../state";
import TransferFrequencyButton from "./TransferFrequencyButton";

export default observer(function ATCMenu(properties: {
	aircraft: AircraftModel;
}) {
	const posthog = usePostHog();
	const { aircraftId, assignedFlightId, controlledBy, callSign } =
		properties.aircraft;

	if (cwpStore.ATCMenuAircraftId !== aircraftId) {
		return null;
	}

	const toggleAssumeFlight = (): void => {
		const flightIsAlreadyAssumed =
			controlledBy === configurationStore.currentCWP;

		posthog?.capture("atc_menu_action", {
			action: flightIsAlreadyAssumed ? "de_assume" : "assume",
			aircraft_id: aircraftId,
			callsign: callSign,
			previous_controller: controlledBy,
			new_controller: flightIsAlreadyAssumed
				? null
				: configurationStore.currentCWP,
			current_cwp: configurationStore.currentCWP,
		});

		if (flightIsAlreadyAssumed) {
			// Handle flight de-assume logic here.
		} else {
			aircraftStore.aircrafts
				.get(aircraftId)
				?.setController(configurationStore.currentCWP);
			handlePublishPromise(
				acceptFlight(
					controlledBy,
					configurationStore.currentCWP,
					assignedFlightId,
				),
			);
			handlePublishPromise(
				persistFrontendFlightController(
					aircraftId,
					configurationStore.currentCWP,
				),
			);
		}

		cwpStore.clearATCMenuAircraftId();
	};

	const handleTrfClick = (aircraftId: string): void => {
		aircraftStore.aircrafts.get(aircraftId)?.setController("FINISHED"); // Setting transfering to next sector as FINISHED for DIALOG
		// Persist to MQTT so the retained message is overwritten; without this, a reconnect
		// would replay the old "CWP1" retained message and add the flight back to the count.
		handlePublishPromise(
			persistFrontendFlightController(aircraftId, "FINISHED"),
		);

		posthog?.capture("atc_menu_action", {
			action: "transfer",
			aircraft_id: aircraftId,
			callsign: callSign,
			transfer_to: "NS",
			frequency: "130.165",
			current_cwp: configurationStore.currentCWP,
		});

		cwpStore.clearATCMenuAircraftId();
	};

	const handleIntegreClick = (aircraftId: string): void => {
		aircraftStore.aircrafts.get(aircraftId)?.degrease();

		posthog?.capture("atc_menu_action", {
			action: "integre",
			aircraft_id: aircraftId,
			callsign: callSign,
			current_cwp: configurationStore.currentCWP,
		});

		cwpStore.clearATCMenuAircraftId();
	};

	const handleDeIntegreClick = (aircraftId: string): void => {
		aircraftStore.aircrafts.get(aircraftId)?.undegrease();

		posthog?.capture("atc_menu_action", {
			action: "de_integre",
			aircraft_id: aircraftId,
			callsign: callSign,
			current_cwp: configurationStore.currentCWP,
		});

		cwpStore.clearATCMenuAircraftId();
	};

	const handleButtonClick = (action: string): void => {
		posthog?.capture("atc_menu_action", {
			action: action.toLowerCase(),
			aircraft_id: aircraftId,
			callsign: callSign,
			current_cwp: configurationStore.currentCWP,
		});

		cwpStore.clearATCMenuAircraftId();

		if (action === "TP") {
			const wasSelected = cwpStore.selectedAircraftIds.has(aircraftId);
			cwpStore.toggleFlightRouteForAircraft(aircraftId);
			cwpStore.toggleSelectedAircraftId(aircraftId);
			setCurrentAircraftId(aircraftId);

			posthog?.capture("aircraft_clicked", {
				aircraft_id: aircraftId,
				callsign: callSign,
				action: wasSelected ? "deselect" : "select",
				altitude: properties.aircraft.lastKnownAltitude,
				speed: properties.aircraft.lastKnownSpeed,
				bearing: properties.aircraft.lastKnownBearing,
				position: {
					lat: properties.aircraft.lastKnownLatitude,
					lon: properties.aircraft.lastKnownLongitude,
				},
				selected_count: cwpStore.selectedAircraftIds.size,
			});
		}
	};

	const handleMeasurementClick = (mode: "sep" | "qdm"): void => {
		// Get selected aircraft IDs, excluding the menu aircraft itself
		const selectedIds = [...cwpStore.selectedAircraftIds].filter(
			(id) => id !== aircraftId,
		);

		// If we have at least one other selected aircraft, create multiple lines immediately
		if (selectedIds.length > 0) {
			if (mode === "sep") {
				sepQdmStore.createMultipleSepLines(aircraftId, selectedIds);
			} else {
				sepQdmStore.createMultipleQdmLines(aircraftId, selectedIds);
			}

			posthog?.capture("atc_menu_action", {
				action: `${mode}_multiple`,
				aircraft_id: aircraftId,
				callsign: callSign,
				target_count: selectedIds.length,
				current_cwp: configurationStore.currentCWP,
			});
		} else {
			// Otherwise, enter the drag-to-select mode
			if (mode === "sep") {
				sepQdmStore.enableSep(aircraftId);
			} else {
				sepQdmStore.enableQdm(aircraftId);
			}

			posthog?.capture("atc_menu_action", {
				action: mode,
				aircraft_id: aircraftId,
				callsign: callSign,
				current_cwp: configurationStore.currentCWP,
			});
		}

		// Close the ATC menu
		cwpStore.clearATCMenuAircraftId();
	};

	const handleSepClick = (): void => {
		handleMeasurementClick("sep");
	};

	const handleQdmClick = (): void => {
		handleMeasurementClick("qdm");
	};

	// +DB button logic: need exactly 1 other aircraft selected to form a pair
	const otherSelectedId = [...cwpStore.selectedAircraftIds].find(
		(id) => id !== aircraftId,
	);
	const hasExactlyOneOther =
		cwpStore.selectedAircraftIds.size === 1 ||
		(cwpStore.selectedAircraftIds.size === 2 &&
			cwpStore.selectedAircraftIds.has(aircraftId));

	// Check if we can create: need a valid pair that doesn't already exist
	const canCreateDatablock =
		hasExactlyOneOther &&
		otherSelectedId !== undefined &&
		!datablockStore.hasPair(aircraftId, otherSelectedId) &&
		!aircraftStore.hasMtcdConflictForPair(aircraftId, otherSelectedId);

	const handleDatablockClick = (): void => {
		if (!canCreateDatablock || !otherSelectedId) {
			return;
		}

		const aircraftIds = [aircraftId, otherSelectedId];

		// Calculate closest point of approach between the two aircraft
		const cpa = trajectoryPredictionStore.findClosestPointOfApproach(
			aircraftId,
			otherSelectedId,
		);

		// Prepare CPA info if available
		const cpaInfo = cpa
			? {
					distanceNM: cpa.distanceNM,
					timeMin: (cpa.time - simulatorStore.timestamp) / 60, // Convert seconds to minutes from now
				}
			: undefined;

		const datablock = datablockStore.createDatablock(aircraftIds, cpaInfo);

		// If the datablock was created and has a startMin, request the agenda to adjust scale
		if (datablock && datablock.startMin > 0) {
			cwpStore.requestAgendaScaleToFit(datablock.startMin);
		}

		posthog?.capture("atc_menu_action", {
			action: "create_datablock",
			aircraft_ids: aircraftIds,
			callsigns: aircraftIds
				.map((id) => aircraftStore.aircrafts.get(id)?.callSign)
				.filter(Boolean),
			closest_separation_nm: cpaInfo?.distanceNM,
			closest_separation_time_min: cpaInfo?.timeMin,
			current_cwp: configurationStore.currentCWP,
		});

		cwpStore.clearATCMenuAircraftId();
	};

	return (
		<div className="bg-neutral-900/60 p-2.5 w-30 ml-1 rounded-b-sm text-gray-200 font-sans flex flex-col items-center border-t border-t-neutral-800">
			<div className="space-y-2 w-full">
				{controlledBy === configurationStore.currentCWP && (
					<TransferFrequencyButton
						aircraft={properties.aircraft}
						onClick={() => handleTrfClick(aircraftId)}
					/>
				)}
				<button
					onClick={toggleAssumeFlight}
					className={`btn btn-xs btn-primary w-full rounded-xs ${controlledBy === configurationStore.currentCWP ? "opacity-50 cursor-not-allowed" : ""}`}
					disabled={controlledBy === configurationStore.currentCWP}
				>
					{controlledBy === configurationStore.currentCWP
						? "DE ASSUME"
						: "ASSUME"}
				</button>
				{properties.aircraft.degreased ? (
					<button
						onClick={() => handleDeIntegreClick(aircraftId)}
						className="btn btn-xs btn-primary w-full rounded-xs"
					>
						DE INTEGRE
					</button>
				) : (
					<button
						onClick={() => handleIntegreClick(aircraftId)}
						className="btn btn-xs btn-primary w-full rounded-xs"
					>
						INTEGRE
					</button>
				)}
				<hr className="border-t-2 border-neutral-700 w-full" />
				<button
					onClick={() => handleButtonClick("TP")}
					className="btn btn-xs btn-primary w-full rounded-xs"
				>
					TP
				</button>
				<button
					onClick={handleSepClick}
					className="btn btn-xs btn-primary w-full rounded-xs"
				>
					SEP
				</button>
				<button
					onClick={handleQdmClick}
					className="btn btn-xs btn-primary w-full rounded-xs"
				>
					QDM
				</button>
				<hr className="border-t-2 border-neutral-700 w-full" />
				<button
					onClick={handleDatablockClick}
					className={`btn btn-xs btn-primary w-full rounded-xs ${canCreateDatablock ? "" : "opacity-50 cursor-not-allowed"}`}
					disabled={!canCreateDatablock}
					title="Create datablock with selected aircraft pair"
				>
					+DB
				</button>
			</div>
		</div>
	);
});
